import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useLayerForkMutation } from "@/v2-backoffice-client/layer/fork/layer.fork.mutation"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { LAYER_QUERY_NAME } from "@/v2-backoffice-client/layer/layer.constants"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

export type LayerForkDialogProps = DialogProps & {
  layer: LayerFragment
}

/**
 * Forks a layer: a full copy of its setup (configuration, filters,
 * interactions, export, table, thumbnail, categories) under a new feature
 * code, so the copy can be taken in a different direction while the
 * original keeps serving its case. The fork lands in the review queue
 * (disabled, unreviewed) and its sheet opens right after, ready to edit.
 */
function LayerForkDialog(props: LayerForkDialogProps) {
  const { layer, open, onOpenChange, ...dialogProps } = props

  const opener = useAppViewSheetOpener()
  const [featureCode, setFeatureCode] = useState("")
  const [name, setName] = useState("")

  // Re-prefill for each opening: sensible defaults the operator overwrites
  // as soon as the fork's own direction has a name.
  useEffect(() => {
    if (!open) return
    setFeatureCode(`${layer.FeatureCode}_Fork`)
    setName(`${layer.Name} (fork)`)
  }, [open, layer.FeatureCode, layer.Name])

  const [forkLayer, { loading, error }] = useLayerForkMutation({
    refetchQueries: [
      LAYER_QUERY_NAME,
      LAYER_COUNTS_QUERY_NAME,
      LAYER_AUTHORING_STATUSES_QUERY_NAME,
    ],
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content className="w-11/12 max-w-screen-sm" align="top">
        <Dialog.Header>
          <Dialog.Title>Fork {layer.Name}</Dialog.Title>
          <Dialog.Description>
            Copies the layer&apos;s entire setup under a new feature code. The
            fork lands in the review queue and can take a different direction
            than the original — neither affects the other afterwards.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-2 text-center" error={error} />

        <form
          id="layer-fork-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            void forkLayer({
              variables: {
                input: {
                  SourceFeatureCode: layer.FeatureCode,
                  FeatureCode: featureCode.trim(),
                  Name: name.trim(),
                },
              },
            }).then((result) => {
              const forked = result.data?.forkLayer
              if (!forked) return
              onOpenChange?.(false)
              toast({
                title: `Forked into ${forked.FeatureCode}.`,
                description:
                  "The fork is in the review queue — take it live when its new direction is ready.",
              })
              opener.open("layer", forked.FeatureCode)
            })
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="layer-fork-code">New feature code</Label>
            <Input
              id="layer-fork-code"
              required
              value={featureCode}
              onChange={(event) => setFeatureCode(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Forked from {layer.FeatureCode}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="layer-fork-name">Name (en-GB)</Label>
            <Input
              id="layer-fork-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </form>

        <Dialog.Footer className="mt-4 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            data-feature-code="UI_BackofficeLayers_Fork_Cancel"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="layer-fork-form"
            iconSrc="code-fork"
            isLoading={loading}
            data-feature-code="UI_BackofficeLayers_Fork_Submit"
          >
            Fork layer
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LayerForkDialog
